import {
	createAsyncThunk,
	createSelector,
	createSlice,
} from "@reduxjs/toolkit";
import INVOICE_STATUS from "../../constants/invoice-status";
import { projectMembersApi } from "../services/projectMembers";
import {
	resolveCashDiscountDate,
	validatePositionQuantities,
} from "../../utils/invoiceV2Utils";
import { showToast } from "../../utils/method";
import { multiplyMoney, sumMoney } from "../../utils/money";
import { DEFAULT_VAT_PERCENTAGE } from "../../constants/vat-config";
import { aufmassApi } from "../services/aufmass";
import { lvApi } from "../services/lvService";
import { invoiceApi } from "../services/invoiceService";
import { selectCurrentProjectId } from "./projectDetailsSlice";

const DEFAULT_SALES_TAX_PERCENTAGE = DEFAULT_VAT_PERCENTAGE;

const createInitialFormState = () => ({
	id: null,
	invoiceNumber: "",
	orderNumber: "",
	orderDate: null,
	invoiceDate: null,
	invoiceArrivalDate: null,
	cashDiscountDate: null,
	billingPeriodFrom: null,
	billingPeriodTo: null,
	isFinalInvoice: false,
	selectedTicketCategory: null,
	salesTaxPercentage: DEFAULT_SALES_TAX_PERCENTAGE,
	isDraft: false,
	projectId: null,
	readonly: false,
	isHistoricalBaseline: false,
	historicalInvoices: [],
	internalReferenceNumber: null,
});

const syncAutoCashDiscountDate = (state) => {
	// Keep explicit backend/manual values untouched.
	if (state.formState.id || state.formState.cashDiscountDate) return;

	// The skonto clock starts from the invoice arrival date (Rechnungseingang).
	const invoiceArrivalDate = state.formState.invoiceArrivalDate;
	const cashDiscount = state.ticketCategory?.details?.cashDiscount;
	const cashDiscountDays = state.ticketCategory?.details?.cashDiscountDays;

	// No cash discount configured → no skonto deadline to derive.
	if (!Number(cashDiscount)) return;
	if (!invoiceArrivalDate || cashDiscountDays == null) return;

	state.formState.cashDiscountDate = resolveCashDiscountDate(
		null,
		invoiceArrivalDate,
		cashDiscountDays,
	);
};

/**
 * Utility function for consistent error handling across async thunks
 * @param {Error} error - The error object
 * @param {string} defaultMessage - Default message if error parsing fails
 * @returns {string} The error message to display
 */
const handleAsyncError = (error, defaultMessage) => {
	const errorMessage =
		error.response?.data?.message || error.message || defaultMessage;
	showToast(errorMessage, { type: "error" });
	return errorMessage;
};

// Async thunks for V2-specific operations

/**
 * Fetch hierarchical LV data by ticket category
 */
export const fetchLVsByTicketCategory = createAsyncThunk(
	"invoiceV2/fetchLVsByTicketCategory",
	async ({ projectId, ticketCategoryId }, { dispatch, rejectWithValue }) => {
		const queryResult = dispatch(
			lvApi.endpoints.getLVsByTicketCategory.initiate({
				projectId,
				ticketCategoryId,
			}),
		);
		try {
			const response = await queryResult.unwrap();
			return response;
		} catch (error) {
			const errorMessage = handleAsyncError(
				error,
				"invoice.lv.error-fetch",
			);
			return rejectWithValue(errorMessage);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

/**
 * Update position-level comments from the invoice screen
 * Calls the same PATCH endpoint as the aufmass side so both screens share one field.
 */
export const updateInvoicePositionCommentsV2 = createAsyncThunk(
	"invoiceV2/updatePositionComments",
	async (
		{ invoiceId, invoicePositionId, positionId, comments },
		{ dispatch, rejectWithValue, getState },
	) => {
		const projectId = selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		const mutationResult = dispatch(
			aufmassApi.endpoints.updatePositionComments.initiate({
				projectId,
				invoiceId,
				positionId: invoicePositionId,
				comments,
			}),
		);
		try {
			await mutationResult.unwrap();
			// Keep Redux state in sync
			dispatch(setInvoicePositionComments({ positionId, comments }));
			return { positionId, comments };
		} catch (error) {
			const errorMessage = handleAsyncError(
				error,
				"aufmass.comment-save-failed",
			);
			return rejectWithValue(errorMessage);
		} finally {
			if (typeof mutationResult.reset === "function") {
				mutationResult.reset();
			}
		}
	},
);

/**
 * Fetch invoice by ID for V2
 */
export const fetchInvoiceByIdV2 = createAsyncThunk(
	"invoiceV2/fetchInvoiceById",
	async (invoiceId, { rejectWithValue, dispatch, getState }) => {
		const projectId = selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		const queryResult = dispatch(
			invoiceApi.endpoints.getInvoiceByIdV2.initiate({ projectId, invoiceId }),
		);
		try {
			const response = await queryResult.unwrap();
			const { invoice: invoiceInfo } = response;

			// For V2, we need to fetch LVs with the structured data
			const ticketCategoryId = invoiceInfo.projectTicketCategoryId;
			const lvProjectId = invoiceInfo.projectId || projectId;

			if (lvProjectId && ticketCategoryId) {
				// Fetch LV data structure
				dispatch(
					fetchLVsByTicketCategory({
						projectId: lvProjectId,
						ticketCategoryId,
					}),
				);
			}

			// Force readonly unless the caller has project-scoped invoice.edit
			// (project admins implicitly do). Reads the cached project member
			// loaded by useProjectPermission - not the old global RBAC right.
			const member = projectMembersApi.endpoints.getCurrentMember.select(
				{ projectId },
			)(getState())?.data;
			const canEdit =
				member?.isProjectAdmin === true ||
				member?.permissions?.invoice?.edit === true;
			if (!canEdit && response.invoice) {
				return {
					...response,
					invoice: { ...response.invoice, readonly: true },
				};
			}

			return response;
		} catch (error) {
			const errorMessage = handleAsyncError(
				error,
				"invoice.error-load-failed",
			);
			return rejectWithValue(errorMessage);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

/**
 * Create invoice for V2
 */
export const createInvoiceV2 = createAsyncThunk(
	"invoiceV2/createInvoice",
	async ({ projectId, invoiceData }, { dispatch, rejectWithValue }) => {
		const mutationResult = dispatch(
			invoiceApi.endpoints.saveInvoiceV2.initiate({
				projectId,
				...invoiceData,
			}),
		);
		try {
			const response = await mutationResult.unwrap();
			showToast("INVOICE.CREATE_SUCCESS", { type: "success" });
			return response;
		} catch (error) {
			const errorMessage = handleAsyncError(
				error,
				"status.error.create",
			);
			return rejectWithValue(errorMessage);
		} finally {
			if (typeof mutationResult.reset === "function") {
				mutationResult.reset();
			}
		}
	},
);

/**
 * Update invoice for V2
 */
export const updateInvoiceV2 = createAsyncThunk(
	"invoiceV2/updateInvoice",
	async ({ invoiceId, invoiceData }, { dispatch, rejectWithValue, getState }) => {
		const projectId = selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		const mutationResult = dispatch(
			invoiceApi.endpoints.updateInvoiceV2.initiate({
				projectId,
				invoiceId,
				...invoiceData,
			}),
		);
		try {
			const response = await mutationResult.unwrap();
			showToast("INVOICE.UPDATE_SUCCESS", { type: "success" });
			return response;
		} catch (error) {
			const errorMessage = handleAsyncError(
				error,
				"status.error.update",
			);
			return rejectWithValue(errorMessage);
		} finally {
			if (typeof mutationResult.reset === "function") {
				mutationResult.reset();
			}
		}
	},
);

/**
 * Autosave invoice for V2
 */
export const autosaveInvoiceV2 = createAsyncThunk(
	"invoiceV2/autosaveInvoice",
	async ({ projectId: projectIdArg, invoiceData }, { dispatch, rejectWithValue, getState }) => {
		const projectId =
			projectIdArg || selectCurrentProjectId(getState());
		if (!projectId) return rejectWithValue("No project context");
		let mutationResult;
		try {
			let response;

			if (invoiceData.id) {
				const invoiceId = invoiceData.id;
				const { id, ...invoiceDataToSend } = invoiceData;
				mutationResult = dispatch(
					invoiceApi.endpoints.updateInvoiceV2.initiate({
						projectId,
						invoiceId,
						...invoiceDataToSend,
					}),
				);
				response = await mutationResult.unwrap();
				console.log(
					"✅ [autosaveInvoiceV2] Update response:",
					response,
				);
			} else {
				mutationResult = dispatch(
					invoiceApi.endpoints.saveInvoiceV2.initiate({
						projectId,
						...invoiceData,
					}),
				);
				response = await mutationResult.unwrap();
				console.log(
					"✅ [autosaveInvoiceV2] Create response:",
					response,
				);
			}

			return response;
		} catch (error) {
			console.error("❌ [autosaveInvoiceV2] Autosave failed:", {
				error: error.message,
				status: error.response?.status,
				data: error.response?.data,
			});
			const errorMessage = handleAsyncError(error, "status.error.update");
			return rejectWithValue(errorMessage);
		} finally {
			if (mutationResult && typeof mutationResult.reset === "function") {
				mutationResult.reset();
			}
		}
	},
);

/**
 * Fetch previous invoices by ticket category for V2
 */
export const fetchPreviousInvoicesV2 = createAsyncThunk(
	"invoiceV2/fetchPreviousInvoices",
	async (
		{ projectId, ticketCategoryId, currentInvoiceId },
		{ rejectWithValue, dispatch },
	) => {
		const queryResult = dispatch(
			invoiceApi.endpoints.getInvoicesByTicketCategory.initiate({
				projectId,
				ticketCategoryId,
				currentInvoiceId,
			}),
		);
		try {
			const response = await queryResult.unwrap();

			const invoices = response?.items || [];

			// Find existing draft invoice - ignore REJECTED invoices
			const existingDraft = invoices.find(
				(invoice) =>
					invoice.status === INVOICE_STATUS.DRAFT &&
					invoice.status !== INVOICE_STATUS.REJECTED,
			);

			// Check if there are unchecked invoices (excluding drafts and rejected)
			const nonDraftInvoices = invoices.filter(
				(invoice) =>
					invoice.status !== INVOICE_STATUS.DRAFT &&
					invoice.status !== INVOICE_STATUS.REJECTED,
			);
			const hasUnchecked = nonDraftInvoices.some(
				(invoice) => invoice.status !== INVOICE_STATUS.FINALIZED,
			);

			// Update the related state using the same logic as V1
			dispatch(setExistingDraftInvoice(existingDraft || null));
			dispatch(setHasUncheckedInvoices(hasUnchecked));

			return invoices;
		} catch (error) {
			return rejectWithValue(
				error.message || "Failed to fetch previous invoices",
			);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

/**
 * Fetch positions from previous invoice with allocation quantities
 */
export const fetchPreviousInvoicePositionsV2 = createAsyncThunk(
	"invoiceV2/fetchPreviousInvoicePositions",
	async (
		{ projectId, ticketCategoryId, currentInvoiceId },
		{ dispatch, rejectWithValue },
	) => {
		const queryResult = dispatch(
			invoiceApi.endpoints.getPreviousInvoicePositionsV2.initiate({
				projectId,
				ticketCategoryId,
				currentInvoiceId,
			}),
		);
		try {
			const response = await queryResult.unwrap();

			// Transform positions to Map for Redux state
			const positionsMap = new Map();
			if (response?.positions) {
				Object.entries(response.positions).forEach(
					([positionId, positionData]) => {
						positionsMap.set(positionId, positionData);
					},
				);
			}

			return {
				positionsMap,
				invoiceMetadata: response?.invoiceMetadata,
			};
		} catch (error) {
			const errorMessage = handleAsyncError(
				error,
				"invoice.error-load-failed",
			);
			return rejectWithValue(errorMessage);
		} finally {
			queryResult.unsubscribe();
		}
	},
);

// Initial state for V2 - organized by logical groupings
const initialState = {
	// Core form state
	formState: createInitialFormState(),

	// Stepper workflow state
	stepper: {
		currentStep: 1, // 1: Category, 2: Historical (conditional), 3: Invoice Details
		completedSteps: [], // Array of completed step numbers
		skipHistoricalStep: false, // True if previous invoices exist
		historicalDataConfirmed: false, // True after user confirms historical data
		maxStepReached: 1, // Tracks the furthest step user has reached
	},

	// Position data management - store complete structured position data
	invoicePositions: new Map(), // Map<positionId, {invoice: {...}, allocation: {...}, lv: {...}}>

	// LV (Leistungsverzeichnis) data
	lvData: {
		lvs: [],
		totalCount: 0,
		loading: false,
		error: null,
	},

	// Ticket category state
	ticketCategory: {
		details: null,
		loading: false,
		error: null,
	},

	// Form validation state
	validation: {
		errors: {
			invoiceNumber: "",
			orderNumber: "",
			orderDate: "",
			invoiceDate: "",
			positions: "",
			positionQuantities: "",
			billingPeriodFrom: "",
			billingPeriodTo: "",
		},
		isValid: false,
		// Field errors stay hidden until the user actually tries to save/finish.
		// Validity is still computed live (for autosave gating) but error
		// messages must not appear on the first keystroke.
		hasAttemptedSubmit: false,
	},

	// Loading and error states
	ui: {
		loading: false,
		saving: false,
		autosaving: false,
		error: null,
	},

	// Change tracking
	changeTracking: {
		isDirty: false,
		dirtyTimestamp: null,
		lastAutosaved: null,
		previousInvoiceLoaded: false,
	},

	// Ticket category selection workflow
	ticketCategorySelection: {
		previousInvoices: [],
		loadingInvoices: false,
		selectionError: "",
		isRejectedInvoice: false,
		hasUncheckedInvoices: false,
		existingDraftInvoice: null,
		isSubmitting: false,
	},

	// V2 structured response data
	v2ResponseData: null,

	// Calculated totals
	totals: {
		lvAmount: 0,
		invoiceAmount: 0,
		taxAmount: 0,
		grossAmount: 0,
		positionCount: 0,
	},
};

// Create the V2 slice
export const invoiceV2Slice = createSlice({
	name: "invoiceV2",
	initialState,
	reducers: {
		// Form field updates with improved validation
		setFormField: (state, action) => {
			const { field, value, skipDirtyFlag = false } = action.payload;
			state.formState[field] = value;

			if (field === "invoiceArrivalDate") {
				syncAutoCashDiscountDate(state);
			}

			if (!skipDirtyFlag) {
				state.changeTracking.isDirty = true;
				state.changeTracking.dirtyTimestamp = Date.now();
			}
		},

		// Reset form to initial state
		resetForm: (state) => {
			state.formState = createInitialFormState();
			state.validation = { ...initialState.validation };
			state.stepper = { ...initialState.stepper };
			state.changeTracking.isDirty = false;
			state.changeTracking.dirtyTimestamp = null;
			state.changeTracking.previousInvoiceLoaded = false;
			state.invoicePositions.clear();
			state.v2ResponseData = null;
			state.totals = { ...initialState.totals };
		},

		// Validation error management
		setValidationError: (state, action) => {
			const { field, error } = action.payload;
			state.validation.errors[field] = error;
		},

		clearValidationErrors: (state) => {
			state.validation.errors = { ...initialState.validation.errors };
		},

		// Position management with complete structure
		setPosition: (state, action) => {
			const { positionId, positionData } = action.payload;
			state.invoicePositions.set(positionId, positionData);

			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},

		setInvoicePositionQuantity: (state, action) => {
			const { positionId, quantity, position } = action.payload;
			const existingPosition = state.invoicePositions.get(positionId);

			if (existingPosition) {
				// Update existing position
				const invoiceQuantity = parseFloat(quantity) || 0;
				const unitPrice =
					existingPosition.lv?.unitPrice || position?.unitPrice || 0;
				const updatedPosition = {
					...existingPosition,
					invoice: {
						...existingPosition.invoice,
						quantity: invoiceQuantity,
						amount: multiplyMoney(invoiceQuantity, unitPrice),
						calculation: multiplyMoney(invoiceQuantity, unitPrice),
					},
				};
				state.invoicePositions.set(positionId, updatedPosition);
			} else {
				// Create new position with structured format and LV data from position
				const unitPrice = position?.unitPrice || 0;
				const invoiceQuantity = parseFloat(quantity) || 0;
				const newPosition = {
					invoicePositionId: null,
					itemNumber: position?.itemNumber || null,
					fromPreviousInvoice: false,
					sourceInvoiceId: null,
					lv: {
						quantity: position?.quantity || 0,
						unitPrice: unitPrice,
						totalPrice: position?.totalPrice || 0,
						unit: position?.unit || null,
						description: position?.description || null,
					},
					invoice: {
						quantity: invoiceQuantity,
						amount: multiplyMoney(invoiceQuantity, unitPrice),
						calculation: multiplyMoney(invoiceQuantity, unitPrice),
						comments: "",
					},
					lastAllocation: {
						aufmassQuantity: 0,
						invoiceQuantity: invoiceQuantity,
						allocated: 0,
						available: 0,
						percentage: 0,
					},
					validations: {
						minAllowed: 0,
						hasAufmass: false,
						respectsMinimum: true,
					},
				};
				state.invoicePositions.set(positionId, newPosition);
			}

			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},

		setInvoicePositionComments: (state, action) => {
			const { positionId, comments } = action.payload;
			const existingPosition = state.invoicePositions.get(positionId);

			if (existingPosition) {
				const updatedPosition = {
					...existingPosition,
					invoice: {
						...existingPosition.invoice,
						comments: comments || "",
					},
				};
				state.invoicePositions.set(positionId, updatedPosition);
			}

			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},

		clearInvoicePositions: (state) => {
			state.invoicePositions.clear();
		},

		// Change tracking management
		markFormDirty: (state) => {
			state.changeTracking.isDirty = true;
			state.changeTracking.dirtyTimestamp = Date.now();
		},

		clearDirtyFlag: (state) => {
			state.changeTracking.isDirty = false;
			state.changeTracking.dirtyTimestamp = null;
		},

		// Ticket category workflow management
		setIsRejectedInvoice: (state, action) => {
			state.ticketCategorySelection.isRejectedInvoice = action.payload;
		},

		setPreviousInvoices: (state, action) => {
			state.ticketCategorySelection.previousInvoices = action.payload;
		},

		setLoadingInvoices: (state, action) => {
			state.ticketCategorySelection.loadingInvoices = action.payload;
		},

		setSelectionError: (state, action) => {
			state.ticketCategorySelection.selectionError = action.payload;
		},

		setIsSubmitting: (state, action) => {
			state.ticketCategorySelection.isSubmitting = action.payload;
		},

		clearTicketCategorySelectionState: (state) => {
			state.ticketCategorySelection = {
				...initialState.ticketCategorySelection,
			};
		},

		setExistingDraftInvoice: (state, action) => {
			state.ticketCategorySelection.existingDraftInvoice = action.payload;
		},

		setHasUncheckedInvoices: (state, action) => {
			state.ticketCategorySelection.hasUncheckedInvoices = action.payload;
		},

		// Form validation with improved logic separation
		validateForm: (state, action) => {
			// Only run validation if form is dirty or explicitly requested
			const forceValidate = action?.payload?.force === true;
			if (state.changeTracking.isDirty || forceValidate) {
				const { isValid, errors } =
					require("../../utils/invoiceV2Utils").validateInvoiceV2FormData(
						state.formState,
						state.invoicePositions,
						state.ticketCategory?.details,
					);
				state.validation.isValid = isValid;

				// A forced run means the user hit Save/Finish - arm error display.
				if (forceValidate) {
					state.validation.hasAttemptedSubmit = true;
				}

				// Surface field-level errors only after a submit attempt. The
				// live (unforced) runs the autosave middleware fires on each
				// keystroke update `isValid` for autosave gating but must not
				// paint required-field errors before the user has tried to save.
				if (state.validation.hasAttemptedSubmit) {
					state.validation.errors = errors;
				}
			}
		},

		// Stepper navigation actions
		setCurrentStep: (state, action) => {
			const newStep = action.payload;
			state.stepper.currentStep = newStep;
			// Update max step reached
			if (newStep > state.stepper.maxStepReached) {
				state.stepper.maxStepReached = newStep;
			}
		},

		completeStep: (state, action) => {
			const stepNumber = action.payload;
			if (!state.stepper.completedSteps.includes(stepNumber)) {
				state.stepper.completedSteps.push(stepNumber);
			}
		},

		setSkipHistoricalStep: (state, action) => {
			state.stepper.skipHistoricalStep = action.payload;
		},

		setHistoricalDataConfirmed: (state, action) => {
			state.stepper.historicalDataConfirmed = action.payload;
		},

		resetStepper: (state) => {
			state.stepper = { ...initialState.stepper };
		},

		// Navigate to specific step (for saved invoices)
		navigateToStep: (state, action) => {
			state.stepper.currentStep = action.payload;
		},
	},

	extraReducers: (builder) => {
		builder
			// Fetch LVs by ticket category
			.addCase(fetchLVsByTicketCategory.pending, (state) => {
				state.lvData.loading = true;
				state.lvData.error = null;
			})
			.addCase(fetchLVsByTicketCategory.fulfilled, (state, action) => {
				state.lvData.loading = false;
				// The request interceptor extracts data.data automatically, so payload is already the data object
				state.lvData.lvs = action.payload?.lvs || [];
				state.lvData.totalCount = action.payload?.totalCount || 0;

				// Update ticket category details if available
				if (action.payload?.ticketCategory) {
					state.ticketCategory.details =
						action.payload.ticketCategory;

					// Update VAT if available
					if (action.payload.ticketCategory.vat !== undefined) {
						state.formState.salesTaxPercentage =
							action.payload.ticketCategory.vat;
					}

					syncAutoCashDiscountDate(state);
				}
			})
			.addCase(fetchLVsByTicketCategory.rejected, (state, action) => {
				state.lvData.loading = false;
				state.lvData.error = action.payload;
			})

			// Fetch invoice by ID
			.addCase(fetchInvoiceByIdV2.pending, (state) => {
				state.ui.loading = true;
				state.ui.error = null;
			})
			.addCase(fetchInvoiceByIdV2.fulfilled, (state, action) => {
				state.ui.loading = false;
				const responseData = action.payload;

				// Handle V2 structured response
				if (responseData?.invoice) {
					const {
						invoice: invoiceInfo,
						formData,
						totals,
					} = responseData;

					// Update all form state fields from invoice data
					state.formState.id = invoiceInfo.id;
					state.formState.invoiceNumber = invoiceInfo.invoiceNumber;
					state.formState.orderNumber = invoiceInfo.orderNumber || "";
					state.formState.orderDate = invoiceInfo.orderDate
						? new Date(invoiceInfo.orderDate)
						: null;
					state.formState.invoiceDate = invoiceInfo.invoiceDate
						? new Date(invoiceInfo.invoiceDate)
						: null;
					state.formState.invoiceArrivalDate =
						invoiceInfo.invoiceArrivalDate
							? new Date(invoiceInfo.invoiceArrivalDate)
							: null;
					state.formState.cashDiscountDate =
						invoiceInfo.cashDiscountDate
							? new Date(invoiceInfo.cashDiscountDate)
							: null;
					state.formState.projectId = invoiceInfo.projectId;
					state.formState.projectTicketCategoryId =
						invoiceInfo.projectTicketCategoryId;
					state.formState.billingPeriodFrom =
						invoiceInfo.billingPeriodFrom
							? new Date(invoiceInfo.billingPeriodFrom)
							: null;
					state.formState.billingPeriodTo =
						invoiceInfo.billingPeriodTo
							? new Date(invoiceInfo.billingPeriodTo)
							: null;
					state.formState.isFinalInvoice = invoiceInfo.isFinalInvoice;
					state.formState.salesTaxPercentage =
						invoiceInfo.salesTaxPercentage;
					state.formState.internalReferenceNumber =
						invoiceInfo.internalReferenceNumber;
					state.formState.readonly = invoiceInfo.readonly;
					state.formState.isDraft =
						invoiceInfo.status === INVOICE_STATUS.DRAFT;
					state.formState.isHistoricalBaseline =
						invoiceInfo.isHistoricalBaseline || false;
					state.formState.historicalInvoices =
						invoiceInfo.historicalInvoices || [];

					// Set selected ticket category from formData
					if (formData?.selectedTicketCategory) {
						state.formState.selectedTicketCategory =
							formData.selectedTicketCategory;
					}

					// Store structured response data for future use
					state.v2ResponseData = responseData;

					// Extract positions directly without transformation
					if (responseData?.positions) {
						// Store positions with complete structure
						state.invoicePositions = new Map(
							Object.entries(responseData.positions),
						);
					}

					// Store totals for display
					if (totals) {
						state.totals = totals;
					}

					// Loaded invoice is authoritative - clear stale validation from bootstrap
					state.validation = { ...initialState.validation };
					state.changeTracking.isDirty = false;
					state.changeTracking.dirtyTimestamp = null;
				}
			})
			.addCase(fetchInvoiceByIdV2.rejected, (state, action) => {
				state.ui.loading = false;
				state.ui.error = action.payload;
			})

			// Create invoice
			.addCase(createInvoiceV2.pending, (state) => {
				state.ui.saving = true;
				state.ui.error = null;
			})
			.addCase(createInvoiceV2.fulfilled, (state, action) => {
				state.ui.saving = false;
				state.changeTracking.isDirty = false;
				state.changeTracking.dirtyTimestamp = null;

				// Update form state with response data
				if (action.payload?.id) {
					state.formState.id = action.payload.id;
				}
				if (action.payload?.invoiceNumber) {
					state.formState.invoiceNumber =
						action.payload.invoiceNumber;
				}
				if (action.payload?.internalReferenceNumber) {
					state.formState.internalReferenceNumber =
						action.payload.internalReferenceNumber;
				}
			})
			.addCase(createInvoiceV2.rejected, (state, action) => {
				state.ui.saving = false;
				state.ui.error = action.payload;
			})

			// Update invoice
			.addCase(updateInvoiceV2.pending, (state) => {
				state.ui.saving = true;
				state.ui.error = null;
			})
			.addCase(updateInvoiceV2.fulfilled, (state, action) => {
				state.ui.saving = false;
				state.changeTracking.isDirty = false;
				state.changeTracking.dirtyTimestamp = null;

				// Update form state with response data if needed
				if (action.payload) {
					state.formState = { ...state.formState, ...action.payload };
				}
			})
			.addCase(updateInvoiceV2.rejected, (state, action) => {
				state.ui.saving = false;
				state.ui.error = action.payload;
			})

			// Autosave invoice
			.addCase(autosaveInvoiceV2.pending, (state) => {
				state.ui.autosaving = true;
			})
			.addCase(autosaveInvoiceV2.fulfilled, (state, action) => {
				state.ui.autosaving = false;
				state.changeTracking.isDirty = false;
				state.changeTracking.dirtyTimestamp = null;
				state.changeTracking.lastAutosaved = Date.now();

				// Update form state with response data
				if (action.payload?.id) {
					state.formState.id = action.payload.id;
				}
				if (action.payload?.invoiceNumber) {
					state.formState.invoiceNumber =
						action.payload.invoiceNumber;
				}
				if (action.payload?.internalReferenceNumber) {
					state.formState.internalReferenceNumber =
						action.payload.internalReferenceNumber;
				}
			})
			.addCase(autosaveInvoiceV2.rejected, (state, action) => {
				console.error(
					"❌ [autosaveInvoiceV2.rejected] Autosave failed:",
					action.payload,
				);
				state.ui.autosaving = false;
				// Don't clear isDirty on autosave failure to allow retry
			})

			// Fetch previous invoices
			.addCase(fetchPreviousInvoicesV2.pending, (state) => {
				state.ticketCategorySelection.loadingInvoices = true;
				state.ticketCategorySelection.selectionError = "";
			})
			.addCase(fetchPreviousInvoicesV2.fulfilled, (state, action) => {
				state.ticketCategorySelection.loadingInvoices = false;
				state.ticketCategorySelection.previousInvoices = action.payload;
			})
			.addCase(fetchPreviousInvoicesV2.rejected, (state, action) => {
				state.ticketCategorySelection.loadingInvoices = false;
				state.ticketCategorySelection.selectionError = action.payload;
			})

			// Fetch previous invoice positions
			.addCase(fetchPreviousInvoicePositionsV2.pending, (state) => {
				state.ui.loading = true;
				state.ui.error = null;
			})
			.addCase(
				fetchPreviousInvoicePositionsV2.fulfilled,
				(state, action) => {
					state.ui.loading = false;

					if (action?.payload?.positionsMap) {
						// Store positions directly with structured format
						state.invoicePositions = action.payload.positionsMap;
						state.changeTracking.previousInvoiceLoaded = true;
					}

					// Populate order fields from previous invoice metadata
					if (action?.payload?.invoiceMetadata) {
						const metadata = action.payload.invoiceMetadata;
						if (metadata.orderNumber) {
							state.formState.orderNumber = metadata.orderNumber;
						}
						if (metadata.orderDate) {
							state.formState.orderDate = new Date(
								metadata.orderDate,
							);
						}
					}
				},
			)
			.addCase(
				fetchPreviousInvoicePositionsV2.rejected,
				(state, action) => {
					state.ui.loading = false;
					state.ui.error = action.payload;
				},
			);
	},
});

// Export actions
export const {
	setFormField,
	resetForm,
	setValidationError,
	clearValidationErrors,
	setPosition,
	setInvoicePositionQuantity,
	setInvoicePositionComments,
	clearInvoicePositions,
	markFormDirty,
	clearDirtyFlag,
	setIsRejectedInvoice,
	setPreviousInvoices,
	setLoadingInvoices,
	setSelectionError,
	setIsSubmitting,
	clearTicketCategorySelectionState,
	setExistingDraftInvoice,
	setHasUncheckedInvoices,
	validateForm,
	setCurrentStep,
	completeStep,
	setSkipHistoricalStep,
	setHistoricalDataConfirmed,
	resetStepper,
	navigateToStep,
} = invoiceV2Slice.actions;

// Optimized selectors with better performance patterns
export const selectFormState = (state) =>
	state.invoiceV2?.formState || initialState.formState;

export const selectLVData = (state) => {
	// V2 approach: LV data comes from the dedicated LV endpoint, not the invoice endpoint
	return state.invoiceV2?.lvData || initialState.lvData;
};

export const selectTicketCategoryDetails = (state) =>
	state.invoiceV2?.ticketCategory?.details || null;

export const selectLvQuantityWarningPercent = (state) => {
	const ticketCategory = state.invoiceV2?.ticketCategory?.details;
	return ticketCategory?.lvQuantityWarningPercent ?? 85;
};

export const selectFormErrors = (state) =>
	state.invoiceV2?.validation?.errors || initialState.validation.errors;

export const selectIsFormValid = (state) =>
	state.invoiceV2?.validation?.isValid || false;

export const selectIsLoading = (state) => state.invoiceV2?.ui?.loading || false;

export const selectIsSaving = (state) => state.invoiceV2?.ui?.saving || false;

export const selectError = (state) => state.invoiceV2?.ui?.error || null;

export const selectIsDirty = (state) =>
	state.invoiceV2?.changeTracking?.isDirty || false;

export const selectDirtyTimestamp = (state) =>
	state.invoiceV2?.changeTracking?.dirtyTimestamp || null;

export const selectAutosaving = (state) =>
	state.invoiceV2?.ui?.autosaving || false;

export const selectLastAutosaved = (state) =>
	state.invoiceV2?.changeTracking?.lastAutosaved || null;

export const selectPreviousInvoiceLoaded = (state) =>
	state.invoiceV2?.changeTracking?.previousInvoiceLoaded || false;

export const selectIsRejectedInvoice = (state) =>
	state.invoiceV2?.ticketCategorySelection?.isRejectedInvoice || false;

export const selectTicketCategorySelectionState = (state) =>
	state.invoiceV2?.ticketCategorySelection ||
	initialState.ticketCategorySelection;

export const selectPreviousInvoices = (state) =>
	state.invoiceV2?.ticketCategorySelection?.previousInvoices || [];

export const selectLoadingInvoicesV2 = (state) =>
	state.invoiceV2?.ticketCategorySelection?.loadingInvoices || false;

export const selectSelectionError = (state) =>
	state.invoiceV2?.ticketCategorySelection?.selectionError || "";

export const selectIsSubmittingV2 = (state) =>
	state.invoiceV2?.ticketCategorySelection?.isSubmitting || false;

export const selectExistingDraftInvoice = (state) =>
	state.invoiceV2?.ticketCategorySelection?.existingDraftInvoice || null;

export const selectHasUncheckedInvoices = (state) =>
	state.invoiceV2?.ticketCategorySelection?.hasUncheckedInvoices || false;

// Position selectors - use native structured data
export const selectInvoicePositions = (state) =>
	state.invoiceV2?.invoicePositions || new Map();

export const selectInvoicePosition = (positionId) => (state) => {
	return state.invoiceV2?.invoicePositions?.get(positionId) || null;
};

export const selectInvoicePositionQuantity = (positionId) => (state) => {
	const position = state.invoiceV2?.invoicePositions?.get(positionId);
	return position?.invoice?.quantity || 0;
};

export const selectInvoicePositionComments = (positionId) => (state) => {
	const position = state.invoiceV2?.invoicePositions?.get(positionId);
	return position?.invoice?.comments || "";
};

export const selectInvoicePositionAufmassQuantity = (positionId) => (state) => {
	const position = state.invoiceV2?.invoicePositions?.get(positionId);
	return position?.lastAllocation?.aufmassQuantity || 0;
};

export const selectInvoicePositionFromPreviousInvoice =
	(positionId) => (state) => {
		const position = state.invoiceV2?.invoicePositions?.get(positionId);
		return position?.fromPreviousInvoice || false;
	};

export const selectInvoicePositionLV = (positionId) => (state) => {
	const position = state.invoiceV2?.invoicePositions?.get(positionId);
	return position?.lv || {};
};

// Memoized complex selectors for better performance
export const selectPositionsWithQuantitiesCount = (state) => {
	const positions = state.invoiceV2?.invoicePositions || new Map();
	return Array.from(positions.values()).filter(
		(position) => position?.invoice?.quantity > 0,
	).length;
};

export const selectTotalAmount = (state) => {
	const invoicePositions = state.invoiceV2?.invoicePositions || new Map();
	return Array.from(invoicePositions.values()).reduce(
		(total, position) => total + (position.invoice?.amount || 0),
		0,
	);
};

export const selectFormValidationState = (state) => ({
	isValid: state.invoiceV2?.validation?.isValid || false,
	errors:
		state.invoiceV2?.validation?.errors || initialState.validation.errors,
	isDirty: state.invoiceV2?.changeTracking?.isDirty || false,
});

// Combined selectors for common UI needs
export const selectInvoiceCreationState = (state) => ({
	saving: state.invoiceV2?.ui?.saving || false,
	error: state.invoiceV2?.ui?.error || null,
	isDirty: state.invoiceV2?.changeTracking?.isDirty || false,
	isValid: state.invoiceV2?.validation?.isValid || false,
});

export const selectChangeTrackingState = createSelector(
	[
		(state) => state.invoiceV2?.changeTracking?.isDirty,
		(state) => state.invoiceV2?.changeTracking?.dirtyTimestamp,
		(state) => state.invoiceV2?.changeTracking?.lastAutosaved,
		(state) => state.invoiceV2?.changeTracking?.previousInvoiceLoaded,
	],
	(isDirty, dirtyTimestamp, lastAutosaved, previousInvoiceLoaded) => ({
		isDirty: isDirty || false,
		dirtyTimestamp: dirtyTimestamp || null,
		lastAutosaved: lastAutosaved || null,
		previousInvoiceLoaded: previousInvoiceLoaded || false,
	}),
);

export const selectTicketCategoryWorkflowState = createSelector(
	[
		(state) => state.invoiceV2?.ticketCategorySelection?.previousInvoices,
		(state) => state.invoiceV2?.ticketCategorySelection?.loadingInvoices,
		(state) => state.invoiceV2?.ticketCategorySelection?.selectionError,
		(state) => state.invoiceV2?.ticketCategorySelection?.existingDraftInvoice,
		(state) => state.invoiceV2?.ticketCategorySelection?.hasUncheckedInvoices,
		(state) => state.invoiceV2?.ticketCategorySelection?.isRejectedInvoice,
	],
	(
		previousInvoices,
		loadingInvoices,
		selectionError,
		existingDraftInvoice,
		hasUncheckedInvoices,
		isRejectedInvoice,
	) => ({
		previousInvoices: previousInvoices || [],
		loading: loadingInvoices || false,
		error: selectionError || "",
		existingDraft: existingDraftInvoice || null,
		hasUnchecked: hasUncheckedInvoices || false,
		isRejected: isRejectedInvoice || false,
	}),
);

// V2 structured response selectors
export const selectV2ResponseData = (state) =>
	state.invoiceV2?.v2ResponseData || null;

// Optimized selector for position pricing data
export const selectV2PositionPricing = (positionId) => (state) => {
	const position = state.invoiceV2?.invoicePositions?.get(positionId);
	if (!position) return null;

	return {
		unitPrice: position.lv?.unitPrice || 0,
		totalPrice: position.lv?.totalPrice || 0,
		unit: position.lv?.unit || "",
		itemNumber: position.lv?.itemNumber || "",
		invoiceQuantity: position.invoice?.quantity || 0,
		invoiceAmount: position.invoice?.amount || 0,
	};
};

// Memoized selector to calculate totals from invoice positions
export const selectV2Totals = createSelector(
	[
		(state) => state.invoiceV2?.invoicePositions,
		(state) => state.invoiceV2?.totals,
	],
	(invoicePositions, fallbackTotals) => {
		if (!invoicePositions || invoicePositions.size === 0) {
			return (
				fallbackTotals || {
					lvAmount: 0,
					invoiceAmount: 0,
					taxAmount: 0,
					grossAmount: 0,
					positionCount: 0,
				}
			);
		}

		let positionCount = 0;

		// Calculate totals from invoice positions
		const invoiceAmounts = [];
		const lvAmounts = [];
		for (const [, positionData] of invoicePositions) {
			const quantity = positionData?.invoice?.quantity || 0;
			const unitPrice = positionData?.lv?.unitPrice || 0;

			// Include positions with any non-zero quantity (supports negative and zero unit prices)
			if (quantity !== 0) {
				invoiceAmounts.push(multiplyMoney(quantity, unitPrice));
				positionCount++;
			}

			// Add LV total price regardless of invoice quantity
			lvAmounts.push(positionData?.lv?.totalPrice || 0);
		}
		const invoiceAmount = sumMoney(invoiceAmounts);
		const lvAmount = sumMoney(lvAmounts);

		return {
			lvAmount,
			invoiceAmount,
			taxAmount: 0, // Will be calculated in InvoiceSummary component
			grossAmount: 0, // Will be calculated in InvoiceSummary component
			positionCount,
		};
	},
);

export const selectV2InvoiceMetadata = (state) =>
	state.invoiceV2?.v2ResponseData?.invoice || null;

export const selectV2UIConfig = (state) => ({
	readonly: state.invoiceV2?.v2ResponseData?.invoice?.readonly || false,
	positionUnitMap: {},
});

// Position validation selector
export const selectPositionValidationErrors = (state) => {
	const invoicePositions = state.invoiceV2?.invoicePositions || new Map();
	return validatePositionQuantities(invoicePositions);
};

// Perform validation and return result synchronously
export const performFormValidation = (state) => {
	const formState = state.invoiceV2?.formState || initialState.formState;
	const invoicePositions = state.invoiceV2?.invoicePositions || new Map();

	const { isValid, errors } =
		require("../../utils/invoiceV2Utils").validateInvoiceV2FormData(
			formState,
			invoicePositions,
			state.invoiceV2?.ticketCategory?.details,
		);

	return { isValid, errors };
};

// Stepper selectors
export const selectCurrentStep = (state) =>
	state.invoiceV2?.stepper?.currentStep || 1;
export const selectCompletedSteps = (state) =>
	state.invoiceV2?.stepper?.completedSteps || [];
export const selectSkipHistoricalStep = (state) =>
	state.invoiceV2?.stepper?.skipHistoricalStep || false;
export const selectHistoricalDataConfirmed = (state) =>
	state.invoiceV2?.stepper?.historicalDataConfirmed || false;
export const selectMaxStepReached = (state) =>
	state.invoiceV2?.stepper?.maxStepReached || 1;
export const selectCanNavigateToStep = (stepNumber) => (state) => {
	const maxStepReached = state.invoiceV2?.stepper?.maxStepReached || 1;
	return stepNumber <= maxStepReached;
};

// Selector for disabled steps - Steps 1 & 2 are locked after invoice is saved
export const selectDisabledStepsMap = createSelector(
	[(state) => state.invoiceV2?.formState?.id],
	(invoiceId) => {
		const map = {};

		if (invoiceId) {
			// Once invoice is saved, configuration steps are locked
			map[1] = "invoice.steps-locked-after-save";
			map[2] = "invoice.steps-locked-after-save";
		}

		return map;
	},
);

// Export the reducer
export default invoiceV2Slice.reducer;
